import * as thunks from './thunks';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  GetTapTapCustomersPayload,
  GetTapTapReportPayload,
  TapTapColumn,
  TapTapCustomerData,
  TapTapCustomerPayment,
  TapTapFiltersV2,
  TapTapOperationDashboardColumn,
  PendingTapTapInternalCustomer,
  TapTapProfile,
  TapTapStatusReport
} from './types';
import {
  currentTapTapVersion,
  taptapCustomerInitialPayload,
  taptapOperationDashboardColumns
} from './constants';
import { initialTaptapTableColumns } from './constants/taptap-table-columns';
import { isInsertNewCustomerDataIsValid } from './utils';
import { cloneDeep } from 'lodash';

type State = {
  taptapProfiles: TapTapProfile[];

  isTapTapCustomersLoading: boolean;
  taptapCustomers: TapTapCustomerData[];
  taptapGetCustomersPayload: GetTapTapCustomersPayload;
  taptapColumns: TapTapColumn[];
  taptapOperationDashboardColumns: TapTapOperationDashboardColumn[];

  taptapCustomerMaxCount: number;
  isNextPageLoading: boolean;
  lastPageReached: boolean;

  taptapVersion?: number;

  pendingInternalCustomer?: PendingTapTapInternalCustomer;

  taptapStatusReport?: TapTapStatusReport[];
  taptapStatusReportPayload: GetTapTapReportPayload;
  isTapTapReportsLoading: boolean;
};

const initialState: State = {
  isTapTapCustomersLoading: false,
  taptapCustomers: [],
  taptapProfiles: [],
  taptapGetCustomersPayload: taptapCustomerInitialPayload,
  taptapColumns: initialTaptapTableColumns,
  taptapOperationDashboardColumns,

  taptapCustomerMaxCount: 0,
  isNextPageLoading: false,
  lastPageReached: false,
  taptapVersion: currentTapTapVersion,
  pendingInternalCustomer: undefined,

  taptapStatusReport: [],
  taptapStatusReportPayload: {
    start_date: null,
    end_date: null,
    filters: {
      logic: 'and',
      conditions: []
    }
  },
  isTapTapReportsLoading: false,
};

const slice = createSlice({
  name: 'taptapCustomer',
  initialState,
  reducers: {
    setPendingInternalCustomer: (
      state,
      { payload }: PayloadAction<PendingTapTapInternalCustomer>
    ) => {
      state.pendingInternalCustomer = payload;
    },
    clearPendingInternalCustomer: (state) => {
      state.pendingInternalCustomer = undefined;
    },
    updateSearchValue: (state, { payload }: PayloadAction<string>) => {
      state.taptapGetCustomersPayload.search = payload || '';
    },
    insertNewTaptapCustomers: (
      state,
      { payload }: PayloadAction<TapTapCustomerData>
    ) => {
      const localGetCustomersPayload = cloneDeep(
        state.taptapGetCustomersPayload
      );

      if (isInsertNewCustomerDataIsValid(payload, localGetCustomersPayload)) {
        state.taptapCustomers.unshift({
          ...payload,
          customers_payments: payload?.customers_payments || []
        });
      }
    },
    setTaptapCustomers: (
      state,
      { payload }: PayloadAction<TapTapCustomerData[]>
    ) => {
      state.taptapCustomers = payload;
    },
    updateTaptapCustomer: (
      state,
      { payload }: PayloadAction<TapTapCustomerData>
    ) => {
      const localGetCustomersPayload = cloneDeep(
        state.taptapGetCustomersPayload
      );
      const index = state.taptapCustomers.findIndex(
        (customer) => customer.id === payload.id
      );
      if (index !== -1) {
        // Do the usual update

        if (
          (!state.taptapCustomers[index]?.is_deleted && payload.is_deleted) ||
          !isInsertNewCustomerDataIsValid(payload, localGetCustomersPayload) // Data already exist on list. But its fot following the local filter so remove it from list.
        ) {
          // Meaning this is an update but got soft deleted during update
          // Dating nasa list pero na soft delete na
          state.taptapCustomers = state.taptapCustomers.filter(
            (item) => item.id !== payload.id
          );
        } else {
          state.taptapCustomers[index] = {
            ...payload,
            customers_payments:
              state.taptapCustomers[index].customers_payments || []
          };
        }
      } else {
        // If not found, insert it.. But check for local filter first
        if (isInsertNewCustomerDataIsValid(payload, localGetCustomersPayload)) {
          state.taptapCustomers.unshift({
            ...payload,
            customers_payments: payload?.customers_payments || []
          });
        }
      }
    },
    removeTaptapCustomer: (
      state,
      { payload }: PayloadAction<TapTapCustomerData>
    ) => {
      state.taptapCustomers = state.taptapCustomers.filter(
        (item) => item.id !== payload.id
      );
    },

    //  Start of customer payment related reducers
    insertNewTaptapCustomerPayment: (
      state,
      { payload }: PayloadAction<TapTapCustomerPayment>
    ) => {
      const customerIndex = state.taptapCustomers?.findIndex(
        (x) => x.id === payload.customer_id
      );
      if (customerIndex !== -1) {
        state.taptapCustomers[customerIndex].customers_payments?.push(payload);
      }
    },
    updateTaptapCustomerPaymentDetails: (
      state,
      { payload }: PayloadAction<TapTapCustomerPayment>
    ) => {
      const customerIndex = state.taptapCustomers.findIndex(
        (customer) => customer.id === payload?.customer_id
      );
      // Check if customer exist
      if (customerIndex !== -1) {
        const customer = state.taptapCustomers[customerIndex];

        if (
          customer?.customers_payments &&
          customer?.customers_payments.length > 0
        ) {
          const customerPaymentIndex = customer.customers_payments.findIndex(
            (payment) => payment.id === payload?.id
          );
          if (customerPaymentIndex !== -1) {
            if (
              !customer.customers_payments[customerPaymentIndex]?.is_deleted &&
              payload.is_deleted
            ) {
              // Meaning this is an update but got soft deleted during update
              // Dating nasa list pero na soft delete na
              customer.customers_payments = customer.customers_payments.filter(
                (item) => item.id !== payload.id
              );
            } else {
              customer.customers_payments[customerPaymentIndex] = payload;
            }
          }
        }
      }
    },
    // DO THIS WHEN SERVER DELETED THE PAYMENT AS IN DELETED NOT SOFT DELETED
    removeTaptapCustomerPayment: (
      state,
      { payload }: PayloadAction<TapTapCustomerPayment>
    ) => {
      const paymentId = payload?.id;

      if (payload?.id) {
        const customer = state.taptapCustomers.find((customer) =>
          customer?.customers_payments?.some(
            (payment) => payment.id === paymentId
          )
        );

        if (customer && customer?.customers_payments) {
          // Remove the payment with the matching paymentId
          customer.customers_payments = customer?.customers_payments.filter(
            (payment) => payment.id !== paymentId
          );
        }
      }
    },
    /**
     * START OF USING FILTER
     */
    updateTaptapCustomerFilter: (
      state,
      { payload }: PayloadAction<TapTapFiltersV2>
    ) => {
      state.taptapGetCustomersPayload.filters = payload;
    },
    updateTaptapCustomerGetPayload: (
      state,
      { payload }: PayloadAction<GetTapTapCustomersPayload>
    ) => {
      state.taptapGetCustomersPayload = payload;
    },

    /**
     * START OF COLUMN
     */
    updateTaptapColumns: (
      state,
      { payload }: PayloadAction<TapTapColumn[]>
    ) => {
      state.taptapColumns = payload;
    },
    resetTaptapColumns: (state) => {
      state.taptapColumns = initialState.taptapColumns;
    },
    updateTaptapOperationDashboardColumns: (
      state,
      { payload }: PayloadAction<TapTapOperationDashboardColumn[]>
    ) => {
      state.taptapOperationDashboardColumns = payload;
    },
    resetTaptapOperationDashboardColumns: (state) => {
      state.taptapOperationDashboardColumns =
        initialState.taptapOperationDashboardColumns;
    },
    resetAll: (state) => {
      // SOLID RESET AHHAHAHA
      state.isTapTapCustomersLoading = initialState.isTapTapCustomersLoading;
      state.taptapCustomers = initialState.taptapCustomers;
      state.taptapProfiles = initialState.taptapProfiles;
      state.taptapGetCustomersPayload = initialState.taptapGetCustomersPayload;
      state.taptapColumns = initialState.taptapColumns;
      state.taptapOperationDashboardColumns =
        initialState.taptapOperationDashboardColumns;

      state.taptapCustomerMaxCount = initialState.taptapCustomerMaxCount;
      state.isNextPageLoading = initialState.isNextPageLoading;
      state.lastPageReached = initialState.lastPageReached;
      state.taptapVersion = initialState.taptapVersion;
    },
    setTapTapVersion: (state, { payload }: PayloadAction<number>) => {
      state.taptapVersion = payload;
    }
  },
  extraReducers(builder) {
    builder.addCase(
      thunks.taptapGetProfilesThunk.fulfilled,
      (state, { payload }) => {
        state.taptapProfiles = payload.data || [];
      }
    );
    builder.addCase(thunks.taptapFetchCustomersThunk.pending, (state) => {
      state.taptapGetCustomersPayload.pagination.page = 1;
      state.lastPageReached = false;
      state.taptapCustomerMaxCount = 0;
      state.isTapTapCustomersLoading = true;
    });
    builder.addCase(
      thunks.taptapFetchCustomersThunk.fulfilled,
      (state, { payload }) => {
        state.isTapTapCustomersLoading = false;
        state.taptapCustomers = payload?.data || [];
        state.taptapCustomerMaxCount = payload?.count || 0;
      }
    );
    builder.addCase(thunks.taptapFetchCustomersThunk.rejected, (state) => {
      state.isTapTapCustomersLoading = false;
    });

    builder.addCase(
      thunks.taptapFetchCustomersNextPageThunk.pending,
      (state) => {
        state.isNextPageLoading = true;
      }
    );
    builder.addCase(
      thunks.taptapFetchCustomersNextPageThunk.fulfilled,
      (state, { payload }) => {
        state.isNextPageLoading = false;
        state.taptapCustomerMaxCount = payload?.count || 0;
        state.taptapCustomers = [
          ...state.taptapCustomers,
          ...(payload.data ?? [])
        ];

        if (
          (state.taptapCustomers || []).length >= state.taptapCustomerMaxCount
        ) {
          state.lastPageReached = true;
        }

        if (
          (state.taptapCustomers || []).length < state.taptapCustomerMaxCount
        ) {
          ++state.taptapGetCustomersPayload.pagination.page;
        }
      }
    );
    builder.addCase(
      thunks.taptapFetchCustomersNextPageThunk.rejected,
      (state) => {
        state.isNextPageLoading = false;
      }
    );
    builder.addCase(thunks.taptapFetchEODReportThunk.pending, (state) => {
      state.isTapTapReportsLoading = true; 
    });

    builder.addCase(
      thunks.taptapFetchEODReportThunk.fulfilled,
      (state, { payload }) => {
        state.taptapStatusReport = payload.data || [];
        state.isTapTapReportsLoading = false;
      }
    );

    builder.addCase(thunks.taptapFetchEODReportThunk.rejected, (state) => {
      state.isTapTapReportsLoading = false;
    });
  }
});

export const reducer = slice.reducer;

export const actions = {
  ...thunks,
  ...slice.actions
};
